import { memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { LadderBand, MatchState, SniperMarket } from '../../types/sniper.types';
import {
  DEFAULT_LADDER_ENABLED,
  DEFAULT_NO_MAX_PRICE,
  DEFAULT_VAR_CONFIRMED_FLOOR_PRICE,
  DEFAULT_VAR_DEFENSE_ENABLED,
  DEFAULT_VAR_EXIT_PRICE,
  DEFAULT_VAR_PREVENTIVE_EXIT_ENABLED,
  DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
  DEFAULT_VAR_REBUY_ENABLED,
  effectiveLadderBands,
  gatePct,
  isArmed,
  ladderInvalidReason,
  normalizeDecimalInput,
  pctToGate,
  targetSummary,
  venueColor,
  venueLabel,
} from '../../utils/sniperArm';
import { SniperLadderPopover, type LadderDraft } from './SniperLadderPopover';
import {
  NO_SIZE_HELP,
  NO_SIZE_LABEL,
  noSizeToInput,
  parseNoSizeInput,
} from '../shared/sniperNoSize';

/**
 * One Sniper market row: venue chip · label · counter/team/line · the four
 * per-row editable params (size / max price / VAR exit price / confidence gate)
 * with a DRAFT + ✓ confirm (mirror of the opener entry table — edits stay local
 * until the operator commits, so a typo isn't written mid-keystroke) · an
 * enabled toggle (commits immediately) · a live ARMED badge computed from the
 * feed-derived `MatchState` (`count == floor(line)`).
 */
export const SniperMarketRow = memo(function SniperMarketRow({
  market,
  state,
  onPatch,
  onDelete,
  busy,
  selected,
  onToggleSelected,
  onDirtyChange,
}: {
  market: SniperMarket;
  /** Feed-derived live state; null until the event is active + streaming. */
  state: MatchState | null;
  onPatch: (patch: {
    size?: number;
    maxPrice?: number;
    varExitPrice?: number;
    varDefenseEnabled?: boolean;
    varPreventiveExitEnabled?: boolean;
    varConfirmedFloorPrice?: number;
    varRebuyEnabled?: boolean;
    ladderEnabled?: boolean;
    ladderBands?: LadderBand[];
    floorPrice?: number;
    varPreventiveExitOffset?: number;
    confidenceGate?: number;
    autoNoEnabled?: boolean;
    noMaxPrice?: number;
    noSize?: number | null;
    enabled?: boolean;
  }) => void;
  onDelete: () => void;
  busy?: boolean;
  /** Whether this row is selected for a bulk action. */
  selected?: boolean;
  /** Toggle this row's selection. When omitted, no selection checkbox renders. */
  onToggleSelected?: () => void;
  /**
   * Report this row's uncommitted-edit state upward. Drafts live in THIS
   * component, so without lifting them the card's bulk Enable cannot know a
   * selected row has unsaved params and would arm it at the OLD values.
   */
  onDirtyChange?: (marketId: string, dirty: boolean) => void;
}) {
  // Draft strings — free text while editing, committed on ✓. The gate is edited
  // as an INTEGER PERCENT (1..100); the persisted `confidenceGate` stays 0..1.
  const [size, setSize] = useState(String(market.size));
  const [maxPrice, setMaxPrice] = useState(String(market.maxPrice));
  // The two-phase VAR-defense params. A persisted `null` predates the field —
  // its EFFECTIVE value is the default the backend coerces to, so that is what
  // the operator sees + edits.
  const [varDefenseEnabled, setVarDefenseEnabled] = useState(
    market.varDefenseEnabled ?? DEFAULT_VAR_DEFENSE_ENABLED,
  );
  const [varPreventiveExitEnabled, setVarPreventiveExitEnabled] = useState(
    market.varPreventiveExitEnabled ?? DEFAULT_VAR_PREVENTIVE_EXIT_ENABLED,
  );
  const [varRebuyEnabled, setVarRebuyEnabled] = useState(
    market.varRebuyEnabled ?? DEFAULT_VAR_REBUY_ENABLED,
  );
  // The exit configuration — the confidence ladder plus the one-shot VAR prices
  // it replaces. Edited together in a popover because the ladder is a TABLE that
  // no longer fits the row, and because the two are alternatives: which pair of
  // prices actually executes depends on `ladderEnabled`.
  const persistedExit = useMemo<LadderDraft>(
    () => ({
      ladderEnabled: market.ladderEnabled ?? DEFAULT_LADDER_ENABLED,
      ladderBands: effectiveLadderBands(market.ladderBands),
      floorPrice: market.floorPrice,
      varPreventiveExitOffset:
        market.varPreventiveExitOffset ?? DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
      varExitPrice: market.varExitPrice ?? DEFAULT_VAR_EXIT_PRICE,
      varConfirmedFloorPrice:
        market.varConfirmedFloorPrice ?? DEFAULT_VAR_CONFIRMED_FLOOR_PRICE,
    }),
    [
      market.ladderEnabled,
      market.ladderBands,
      market.floorPrice,
      market.varPreventiveExitOffset,
      market.varExitPrice,
      market.varConfirmedFloorPrice,
    ],
  );
  const [exitCfg, setExitCfg] = useState<LadderDraft>(persistedExit);
  // Auto-NO period-close tail. `autoNoEnabled` is fail-safe-defaulted false at
  // the schema level (never `null`), so unlike the VAR-defense booleans above
  // it needs no `?? DEFAULT` fallback. `noMaxPrice` mirrors `varExitPrice`: a
  // legacy row predating the field shows the backend's coerced default.
  const [autoNoEnabled, setAutoNoEnabled] = useState(market.autoNoEnabled);
  const [noMaxPrice, setNoMaxPrice] = useState(
    String(market.noMaxPrice ?? DEFAULT_NO_MAX_PRICE),
  );
  // `noSize` is NULL-MEANINGFUL (blank = "inherit the main Size", a real valid
  // state resolved by the engine) — unlike every price field above, it must
  // NOT coerce a blank/absent value to a DEFAULT constant, or the draft could
  // never round-trip back to null (the exact bug the split-path-configs
  // Wait/Hedge fields hit). `noSizeToInput`/`parseNoSizeInput` are the shared
  // raw-preserving mapping (mirrors `counterStuckSeconds`).
  const [noSize, setNoSize] = useState(noSizeToInput(market.noSize));
  const [gate, setGate] = useState(String(gatePct(market.confidenceGate)));

  // Re-seed each draft from the persisted row INDEPENDENTLY — a single effect
  // over all four would reset every draft whenever any ONE of them changed
  // server-side, discarding the other three uncommitted edits.
  useEffect(() => {
    setSize(String(market.size));
  }, [market.size]);
  useEffect(() => {
    setMaxPrice(String(market.maxPrice));
  }, [market.maxPrice]);
  useEffect(() => {
    setExitCfg(persistedExit);
  }, [persistedExit]);
  useEffect(() => {
    setVarDefenseEnabled(market.varDefenseEnabled ?? DEFAULT_VAR_DEFENSE_ENABLED);
  }, [market.varDefenseEnabled]);
  useEffect(() => {
    setVarPreventiveExitEnabled(
      market.varPreventiveExitEnabled ?? DEFAULT_VAR_PREVENTIVE_EXIT_ENABLED,
    );
  }, [market.varPreventiveExitEnabled]);
  useEffect(() => {
    setVarRebuyEnabled(market.varRebuyEnabled ?? DEFAULT_VAR_REBUY_ENABLED);
  }, [market.varRebuyEnabled]);
  useEffect(() => {
    setAutoNoEnabled(market.autoNoEnabled);
  }, [market.autoNoEnabled]);
  useEffect(() => {
    setNoMaxPrice(String(market.noMaxPrice ?? DEFAULT_NO_MAX_PRICE));
  }, [market.noMaxPrice]);
  useEffect(() => {
    setNoSize(noSizeToInput(market.noSize));
  }, [market.noSize]);
  useEffect(() => {
    setGate(String(gatePct(market.confidenceGate)));
  }, [market.confidenceGate]);

  const parsed = useMemo(() => {
    const s = Number(size);
    const m = Number(maxPrice);
    // `varExitPrice`/`varConfirmedFloorPrice` moved into `exitCfg` (the ladder
    // popover's draft) — they're no longer standalone row state.
    const { varExitPrice: v, varConfirmedFloorPrice: f } = exitCfg;
    const n = Number(noMaxPrice);
    // `noSize` is null-meaningful (blank = valid "inherit `size`"), so — unlike
    // every other numeric field here — its parse result can't collapse
    // "empty" and "invalid" onto the same `null` sentinel. Keep the resolved
    // value (only safe to read when `noSizeValid`) separate from validity.
    const parsedNoSize = parseNoSizeInput(noSize);
    // Percent → 0..1 gate; `null` for empty / non-numeric / out-of-range.
    const gateValue = gate.trim() === '' ? null : pctToGate(Number(gate));
    return {
      size: s,
      maxPrice: m,
      noMaxPrice: n,
      noSize: parsedNoSize.ok ? parsedNoSize.value : null,
      gate: gateValue,
      valid:
        // size >= 1 mirrors the backend DTO's `@Min(1)` (and the bulk bar) — a
        // fractional size only earns a 400.
        Number.isFinite(s) &&
        s >= 1 &&
        Number.isFinite(m) &&
        m > 0 &&
        m <= 1 &&
        Number.isFinite(v) &&
        v > 0 &&
        v <= 1 &&
        // The Phase-2 floor is a price like varExitPrice — same (0,1] rule.
        Number.isFinite(f) &&
        f > 0 &&
        f <= 1 &&
        // The auto-NO ceiling is a price like maxPrice/varExitPrice — same
        // (0,1] rule, validated unconditionally (like Floor $) so a bad value
        // can never sit behind the toggle waiting to be flipped on.
        Number.isFinite(n) &&
        n > 0 &&
        n <= 1 &&
        // A ladder is only valid as a whole; the popover already refuses to
        // Apply a broken one, so this only guards a row that arrived broken.
        (!exitCfg.ladderEnabled ||
          ladderInvalidReason(
            exitCfg.ladderBands,
            exitCfg.floorPrice,
            exitCfg.varPreventiveExitOffset,
          ) === null) &&
        parsedNoSize.ok &&
        gateValue !== null,
    };
  }, [size, maxPrice, exitCfg, noMaxPrice, noSize, gate]);

  const exitDirty =
    exitCfg.ladderEnabled !== persistedExit.ladderEnabled ||
    exitCfg.floorPrice !== persistedExit.floorPrice ||
    exitCfg.varPreventiveExitOffset !== persistedExit.varPreventiveExitOffset ||
    exitCfg.varExitPrice !== persistedExit.varExitPrice ||
    exitCfg.varConfirmedFloorPrice !== persistedExit.varConfirmedFloorPrice ||
    JSON.stringify(exitCfg.ladderBands) !== JSON.stringify(persistedExit.ladderBands);

  const dirty =
    parsed.size !== market.size ||
    parsed.maxPrice !== market.maxPrice ||
    exitDirty ||
    varDefenseEnabled !==
      (market.varDefenseEnabled ?? DEFAULT_VAR_DEFENSE_ENABLED) ||
    varPreventiveExitEnabled !==
      (market.varPreventiveExitEnabled ?? DEFAULT_VAR_PREVENTIVE_EXIT_ENABLED) ||
    varRebuyEnabled !== (market.varRebuyEnabled ?? DEFAULT_VAR_REBUY_ENABLED) ||
    autoNoEnabled !== market.autoNoEnabled ||
    parsed.noMaxPrice !== (market.noMaxPrice ?? DEFAULT_NO_MAX_PRICE) ||
    parsed.noSize !== market.noSize ||
    Number(gate) !== gatePct(market.confidenceGate);

  const armed = state
    ? isArmed(market.counter, state, market.line, market.team, market.period)
    : false;

  // Only the OFF→ON flip is gated by uncommitted edits (see the switch below).
  const blockedArm = dirty && !market.enabled;

  useEffect(() => {
    onDirtyChange?.(market._id, dirty);
  }, [dirty, market._id, onDirtyChange]);
  // A row that unmounts (deleted, or filtered out of the card) must not leave a
  // stale dirty flag behind that would block a later bulk Enable forever.
  useEffect(
    () => () => {
      onDirtyChange?.(market._id, false);
    },
    [market._id, onDirtyChange],
  );

  const commit = () => {
    if (!dirty || !parsed.valid || parsed.gate === null) return;
    onPatch({
      size: parsed.size,
      maxPrice: parsed.maxPrice,
      varExitPrice: exitCfg.varExitPrice,
      varDefenseEnabled,
      varPreventiveExitEnabled,
      varConfirmedFloorPrice: exitCfg.varConfirmedFloorPrice,
      varRebuyEnabled,
      ladderEnabled: exitCfg.ladderEnabled,
      ladderBands: exitCfg.ladderBands,
      // Only sent with the ladder ON: the field is REQUIRED in that case and
      // meaningless otherwise, and the patch DTO has no way to express "unset".
      ...(exitCfg.ladderEnabled && exitCfg.floorPrice != null
        ? { floorPrice: exitCfg.floorPrice }
        : {}),
      varPreventiveExitOffset: exitCfg.varPreventiveExitOffset,
      autoNoEnabled,
      noMaxPrice: parsed.noMaxPrice,
      noSize: parsed.noSize,
      confidenceGate: parsed.gate,
    });
  };

  // Shared compact styling for the four param inputs — tighter than the MUI
  // `small` default so more rows fit on screen.
  const numFieldSx = {
    '& .MuiInputBase-input': { py: 0.35, px: 0.75, fontSize: 12 },
    '& .MuiInputLabel-root': { fontSize: 10.5 },
    '& .MuiInputLabel-shrink': { fontSize: 11 },
  };

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      useFlexGap
      sx={{
        py: 0.4,
        px: 0.75,
        borderRadius: 1,
        border: 1,
        borderColor: armed ? '#ff5252' : 'divider',
        bgcolor: armed ? 'rgba(255,82,82,0.08)' : 'transparent',
        flexWrap: 'wrap',
      }}
    >
      {onToggleSelected && (
        // NOT disabled by `busy`: selection is local state with no write of its
        // own, so a single-row save must not freeze selection across the card.
        <Checkbox
          size="small"
          checked={!!selected}
          onChange={onToggleSelected}
          inputProps={{ 'aria-label': 'select market for bulk edit' }}
          sx={{ p: 0.25 }}
        />
      )}
      <Chip
        size="small"
        label={venueLabel(market.venue)}
        sx={{
          bgcolor: venueColor(market.venue),
          color: '#fff',
          fontWeight: 700,
          height: 18,
          fontSize: 11,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
      <Box sx={{ minWidth: 160, flex: 1 }}>
        <Typography variant="body2" sx={{ color: 'common.white', fontSize: 12.5, lineHeight: 1.25 }}>
          {market.marketLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, lineHeight: 1.2 }}>
          {targetSummary(market.counter, market.line, market.team, market.period)}
        </Typography>
      </Box>

      {/* ARMED badge — live, from the feed-derived state. */}
      {armed && (
        <Chip
          size="small"
          label="ARMED"
          sx={{
            bgcolor: '#ff5252',
            color: '#fff',
            fontWeight: 800,
            letterSpacing: 0.5,
            height: 18,
            fontSize: 10.5,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      )}

      <TextField
        size="small"
        label="Size"
        value={size}
        onChange={(e) => setSize(normalizeDecimalInput(e.target.value))}
        inputProps={{ inputMode: 'decimal', 'aria-label': 'sweep size' }}
        sx={{ width: 60, ...numFieldSx }}
      />
      <TextField
        size="small"
        label="Max $"
        value={maxPrice}
        onChange={(e) => setMaxPrice(normalizeDecimalInput(e.target.value))}
        inputProps={{ inputMode: 'decimal', 'aria-label': 'max price' }}
        sx={{ width: 60, ...numFieldSx }}
      />
      <SniperLadderPopover
        value={exitCfg}
        onApply={setExitCfg}
        disabled={busy}
        venueSupportsLadder={market.venue === 'kalshi'}
      />
      <Tooltip title="Auto-NO period-close tail — worst per-contract price the NO buy may pay">
        <TextField
          size="small"
          label="No $"
          value={noMaxPrice}
          onChange={(e) => setNoMaxPrice(normalizeDecimalInput(e.target.value))}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'auto-no max price' }}
          sx={{ width: 60, ...numFieldSx }}
        />
      </Tooltip>
      <Tooltip title={NO_SIZE_HELP}>
        <TextField
          size="small"
          label={NO_SIZE_LABEL}
          placeholder="= Size"
          value={noSize}
          onChange={(e) => setNoSize(normalizeDecimalInput(e.target.value))}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'auto-no size' }}
          sx={{ width: 60, ...numFieldSx }}
        />
      </Tooltip>
      <RowToggle
        label="Def"
        tooltip="VAR defense master switch"
        ariaLabel="var defense enabled"
        checked={varDefenseEnabled}
        onChange={setVarDefenseEnabled}
        disabled={busy}
      />
      <RowToggle
        label="Prev"
        tooltip="Phase 1 — precautionary sell at the configured exit price"
        ariaLabel="var preventive exit enabled"
        checked={varPreventiveExitEnabled}
        onChange={setVarPreventiveExitEnabled}
        disabled={busy}
      />
      <RowToggle
        label="Rebuy"
        tooltip="All-clear rebuy if the VAR confirms the goal"
        ariaLabel="var rebuy enabled"
        checked={varRebuyEnabled}
        onChange={setVarRebuyEnabled}
        disabled={busy}
      />
      <RowToggle
        label="Auto-NO"
        tooltip="Auto-NO period-close tail — buy the NO the instant the period ends without the Over crossing"
        ariaLabel="auto-no enabled"
        checked={autoNoEnabled}
        onChange={setAutoNoEnabled}
        disabled={busy}
      />
      <TextField
        size="small"
        label="Gate %"
        value={gate}
        onChange={(e) => setGate(e.target.value.replace(/[^0-9]/g, ''))}
        inputProps={{
          inputMode: 'numeric',
          maxLength: 3,
          'aria-label': 'confidence gate percent',
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: 11 } }}>
              %
            </InputAdornment>
          ),
        }}
        sx={{ width: 78, ...numFieldSx }}
      />

      <Tooltip title={parsed.valid ? 'Save changes' : 'Fix the values first'}>
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={commit}
            disabled={!dirty || !parsed.valid || busy}
            aria-label="confirm market params"
            sx={{ p: 0.5 }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* ARMING is blocked while this row has UNCOMMITTED param edits: the switch
          writes only {enabled}, so it would arm the market at the OLD size/gate
          while the inputs display the new ones. Turning it OFF is never blocked —
          off can't make a market fire, and gating the safe direction would put
          friction on the one action an operator may need in a hurry. */}
      <Tooltip
        title={
          blockedArm
            ? 'Confirm (✓) or revert the edited params before enabling'
            : market.enabled
              ? 'Enabled'
              : 'Disabled (fail-safe OFF)'
        }
      >
        <span>
          <Switch
            size="small"
            checked={market.enabled}
            onChange={(e) => onPatch({ enabled: e.target.checked })}
            disabled={busy || blockedArm}
            inputProps={{ 'aria-label': 'enable market' }}
          />
        </span>
      </Tooltip>

      <Tooltip title="Delete market">
        <IconButton
          size="small"
          onClick={onDelete}
          disabled={busy}
          aria-label="delete market"
          sx={{ p: 0.5 }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
});

/**
 * A compact draft toggle for a VAR-defense boolean. Part of the row DRAFT (like
 * the numeric params) — it flips local state, marks the row dirty, and is
 * written only on ✓. A tiny white caption sits above the switch so the operator
 * can tell Def / Prev / Rebuy apart in the dense row.
 */
function RowToggle({
  label,
  tooltip,
  ariaLabel,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  tooltip: string;
  ariaLabel: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Tooltip title={tooltip}>
      <Stack alignItems="center" spacing={0} sx={{ flexShrink: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: 'common.white', fontSize: 9, lineHeight: 1 }}
        >
          {label}
        </Typography>
        <Switch
          size="small"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          inputProps={{ 'aria-label': ariaLabel }}
          sx={{ m: 0 }}
        />
      </Stack>
    </Tooltip>
  );
}
